/**
 * Map system import fields to headers found in the uploaded CSV/Excel file.
 */
import React from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  SelectPlaceholder,
  Typography,
} from '@/shared/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import type { ImportColumnField } from './suggestColumnMapping';

type ImportColumnMapTableProps = {
  fields: ImportColumnField[];
  headers: string[];
  sampleRows: Array<Record<string, string>>;
  mapping: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
  disabled?: boolean;
  labels: {
    field: string;
    source: string;
    sample: string;
    skip: string;
  };
};

const sampleForHeader = (
  header: string,
  sampleRows: Array<Record<string, string>>
): string =>
  sampleRows
    .map((row) => (row[header] || '').trim())
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');

const ImportColumnMapTable: React.FC<ImportColumnMapTableProps> = ({
  fields,
  headers,
  sampleRows,
  mapping,
  onChange,
  disabled,
  labels,
}) => {
  const usedByField = new Map<string, string>();
  for (const [field, source] of Object.entries(mapping)) {
    if (source) usedByField.set(source, field);
  }

  const setFieldSource = (key: string, source: string): void => {
    onChange({ ...mapping, [key]: source });
  };

  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width="28%">{labels.field}</TableCell>
            <TableCell width="36%">{labels.source}</TableCell>
            <TableCell width="36%">{labels.sample}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field) => {
            const selected = mapping[field.key] || '';
            const sample = selected ? sampleForHeader(selected, sampleRows) : '';
            return (
              <TableRow key={field.key}>
                <TableCell>
                  {field.label}
                  {field.required === true ? ' *' : null}
                </TableCell>
                <TableCell>
                  <FormControl fullWidth sx={{ minWidth: 0 }}>
                    <Select
                      size="small"
                      displayEmpty
                      disabled={disabled}
                      value={selected}
                      onChange={(event) => setFieldSource(field.key, String(event.target.value))}
                      renderValue={(value) =>
                        value ? (
                          String(value)
                        ) : (
                          <SelectPlaceholder>{labels.skip}</SelectPlaceholder>
                        )
                      }
                    >
                      <MenuItem value="">{labels.skip}</MenuItem>
                      {headers.map((header) => {
                        const takenBy = usedByField.get(header);
                        const taken = Boolean(takenBy && takenBy !== field.key);
                        return (
                          <MenuItem key={header} value={header} disabled={taken}>
                            {header}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={sample || undefined}
                  >
                    {sample || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ImportColumnMapTable;
